import { lazy, Suspense, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Clapperboard, Loader2, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SbStage, StoryboardHistory } from '@agentic-ivy/shared-types';
import { useTheme } from '@/hooks/useTheme';
import { studioBOn } from '@/lib/studioB/flag';
import { WorkspaceViewBar } from '../WorkspaceViewBar';
import { StudioBarSlot } from '../studioBarSlot';
import { StoryboardPageMenu } from './StoryboardPageMenu';
import { storyboardPages } from './storyboardPages';
import { StoryboardInputsRail } from './StoryboardInputsRail';
import { lookChosenFrom } from '@/lib/designPackAccess';
import { useSourceProgress, useStoryboard, useStoryboardInputs, type SourceProgress } from './useStoryboardData';
import { useStoryboardBridge } from './useStoryboardBridge';
import { freshVersion } from './feedbackWords';

interface StoryboardPanelProps {
  conversationId: string;
  onPendingChange?: (pending: number) => void;
  /** The view bar's switch and progress, drawn over the storyboard so they share its column. */
  viewBar?: { left?: ReactNode; center?: ReactNode };
  /** Open this version: a feedback card in Ivy's thread points at the page it was sent from. A new object asks again. */
  openVersion?: { version: number };
  /** The page in the frame has drawn (its own sb:ready), so the studio can show it. */
  onPageReady?: () => void;
}

export const UNSENT_CHANGES_PROMPT = 'You have storyboard changes that are not sent yet. Leave anyway?';

/** Below this the feedback rail starts closed, so the storyboard keeps its width next to the chat. */
const RAIL_QUERY = '(min-width: 1100px)';

function useRailOpen() {
  const [open, setOpen] = useState(() => window.matchMedia(RAIL_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(RAIL_QUERY);
    const follow = (e: MediaQueryListEvent) => setOpen(e.matches);
    mql.addEventListener('change', follow);
    return () => mql.removeEventListener('change', follow);
  }, []);
  return [open, setOpen] as const;
}

const AllLooksDialog = lazy(() => import('@/components/chat/TemplateSelectionModal').then((m) => ({ default: m.TemplateSelectionModal })));

export function StoryboardPanel({ conversationId, onPendingChange, viewBar, openVersion, onPageReady }: StoryboardPanelProps) {
  const { info, loading } = useStoryboard(conversationId);
  const { inputs, refetch: refetchInputs } = useStoryboardInputs(conversationId);
  const sources = useSourceProgress(conversationId);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [pending, setPendingState] = useState(0);
  const [showRail, setShowRail] = useRailOpen();
  const [page, setPage] = useState<{ stage: SbStage | null; history: StoryboardHistory | null }>({ stage: null, history: null });
  const isB = studioBOn();
  const barSlot = useContext(StudioBarSlot);

  const onPendingRef = useRef(onPendingChange);
  onPendingRef.current = onPendingChange;
  const unsent = useRef(0);
  const setPending = useCallback((n: number) => {
    unsent.current = n;
    setPendingState(n);
    onPendingRef.current?.(n);
  }, []);

  useEffect(() => {
    if (info.current == null) return;
    setSelected((prev) => (prev == null || pending === 0 ? info.current : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.current]);

  useEffect(() => () => onPendingRef.current?.(0), []);

  const loaded = info.current != null;
  useEffect(() => {
    if (!openVersion || !loaded) return;
    if (unsent.current > 0 && !window.confirm(UNSENT_CHANGES_PROMPT)) return;
    setPending(0);
    setSelected(openVersion.version);
  }, [openVersion, loaded, setPending]);

  const version = info.versions.find((v) => v.version === selected) ?? null;
  const viewUrl = version?.viewUrl ?? null;
  const { theme, isDarkMode } = useTheme();
  // The page reads the app's theme from the fragment before the bridge is up; a fragment change does not reload the frame.
  const frameSrc = viewUrl ? `${viewUrl}#sb-theme=${theme}&sb-dark=${isDarkMode ? 1 : 0}` : null;

  const [seeLooks, setSeeLooks] = useState(false);
  const bridge = useStoryboardBridge({
    conversationId,
    frameRef,
    viewUrl,
    version: version?.version ?? null,
    onPendingChange: setPending,
    onSent: () => refetchInputs(),
    onNavigate: (v) => selectVersion(v),
    onSeeLooks: () => setSeeLooks(true),
    onPage: (p) => {
      setPage(p);
      onPageReady?.();
    },
    fresh: freshVersion(inputs, info.versions, version?.version ?? null),
  });

  const selectVersion = (v: number) => {
    if (v === selected) return;
    if (pending > 0 && !window.confirm(UNSENT_CHANGES_PROMPT)) return;
    setPending(0);
    setSelected(v);
  };

  if (loading) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {!isB && <WorkspaceViewBar column left={viewBar?.left} center={viewBar?.center} />}
        <div className="flex-1 min-h-0 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (!info.versions.length) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {!isB && <WorkspaceViewBar column left={viewBar?.left} center={viewBar?.center} />}
        <StoryboardEmpty sources={sources} />
      </div>
    );
  }

  const newer = info.current != null && selected != null && info.current > selected;

  const pages = storyboardPages(page.history, info.versions, selected);
  const controls = (
    <>
      {newer && (
        <Button variant="outline" size="sm" className="view-bar-newer h-7 text-xs whitespace-nowrap" onClick={() => selectVersion(info.current!)}>
          Newer version · Open
        </Button>
      )}
      <StoryboardPageMenu pages={pages} onOpen={selectVersion} pending={pending} />
      {!isB && (
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => setShowRail((v) => !v)}
        title={showRail ? 'Hide your feedback' : 'Show your feedback'}
        aria-label={showRail ? 'Hide your feedback' : 'Show your feedback'}
      >
        {showRail ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
      </Button>
      )}
    </>
  );

  return (
    <div className="flex-1 min-h-0 flex">
      <div className="flex-1 min-w-0 flex flex-col">
        {isB ? barSlot && createPortal(<div className="flex items-center gap-1.5">{controls}</div>, barSlot) : <WorkspaceViewBar column left={viewBar?.left} center={viewBar?.center} right={controls} />}
        <div {...(isB ? { 'data-storyboard-panel': '' } : {})} className={isB ? 'flex-1 min-h-0' : 'flex-1 min-h-0 bg-white dark:bg-zinc-900'}>
          {viewUrl && (
            <iframe
              key={viewUrl}
              ref={frameRef}
              src={frameSrc ?? undefined}
              title={`Storyboard v${selected}`}
              sandbox="allow-scripts allow-same-origin"
              className="w-full h-full border-0"
              onLoad={() => setPending(0)}
            />
          )}
        </div>
      </div>
      {seeLooks && (
        <Suspense fallback={null}>
          <AllLooksDialog
            open
            onOpenChange={setSeeLooks}
            designSystemOnly
            hideColors
            selectedTemplateId="blank-canvas"
            onTemplateSelect={(_id, _label, _preview, _palette, ref, name) => {
              if (ref && name) bridge.lookChosen(lookChosenFrom(ref, name));
            }}
          />
        </Suspense>
      )}
      {showRail && !isB && <StoryboardInputsRail inputs={inputs} versions={info.versions} selectedVersion={selected} onSelectVersion={selectVersion} />}
    </div>
  );
}

function StoryboardEmpty({ sources }: { sources: SourceProgress[] }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-8">
      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
        <Clapperboard className="w-8 h-8 text-gray-400 dark:text-zinc-500" />
      </div>
      <h3 className="text-base font-medium text-gray-600 dark:text-zinc-400 mb-1">No storyboard yet</h3>
      <p className="text-sm text-gray-400 dark:text-zinc-500 max-w-[340px]">
        Ivy writes one here when there is something to decide — your footage read, the rough cut, the look.
      </p>
      {sources.length > 0 && (
        <ul className="mt-6 w-full max-w-sm space-y-2 text-left">
          {sources.map((s) => (
            <li key={s.sourceId} className="text-xs">
              <div className="flex justify-between text-gray-600 dark:text-zinc-300">
                <span className="font-mono truncate">{s.sourceId}</span>
                <span>
                  {s.step} · {Math.round(s.pct)}%
                </span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, s.pct)}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
